import { useMemo } from 'react';
import { useGetShippingStatusOptionsQuery } from '@/pages/Business/pages/LogisticOperator/services/shipping-statuses.service';
import { Option } from '@/components/inputs/AutocompleteInput';

interface UseShippingStatusesAutocompleteProps {
  search?: string;
  minLength?: number;
  enabled?: boolean;
}

const useShippingStatusesAutocomplete = ({
  search,
  minLength = 3,
  enabled = true,
}: UseShippingStatusesAutocompleteProps = {}) => {
  const searchTextTrimmed = (search ?? '').trim();
  const meetsMinimumLengthForStatusSearch =
    searchTextTrimmed.length >= minLength;

  const { data: statusesResponse, isLoading: isShippingStatusesLoading } =
    useGetShippingStatusOptionsQuery({ limit: 1000, offset: 0 }, { skip: !enabled });

  const shippingStatusOptions: Option[] = useMemo(() => {
    if (!enabled || !meetsMinimumLengthForStatusSearch) return [];
    if (!statusesResponse?.items?.length) return [];

    const queryNormalized = searchTextTrimmed.toLocaleLowerCase('es');

    const matchedStatuses = statusesResponse.items.filter((status) => {
      const commercialNameNormalized = (
        status.commercialName ?? ''
      ).toLocaleLowerCase('es');
      const internalNameNormalized = (status.name ?? '').toLocaleLowerCase(
        'es'
      );
      return (
        commercialNameNormalized.includes(queryNormalized) ||
        internalNameNormalized.includes(queryNormalized)
      );
    });

    return matchedStatuses.map((status) => ({
      label: status.commercialName || 'N/A',
      data: status.id,
    }));
  }, [
    enabled,
    meetsMinimumLengthForStatusSearch,
    searchTextTrimmed,
    statusesResponse?.items,
  ]);

  return {
    shippingStatusOptions,
    isShippingStatusesLoading,
  };
};

export default useShippingStatusesAutocomplete;
